import React from "react";
import {
  Clock,
  PlayCircle,
  StopCircle,
  Cog,
  CheckCircle2,
  Ban,
  XCircle,
  HelpCircle,
} from "lucide-react";

/**
 * Mapa de status da avaliação -> configuração visual (label, cores, ícone).
 * Fonte de verdade ÚNICA para todo o sistema.
 * Reutilizado em: Evaluations.tsx, DocumentContext.tsx
 */
export const EVALUATION_STATUS_CONFIG: Record<
  string,
  { label: string; bgClass: string; textClass: string; borderClass: string; icon: React.ReactNode }
> = {
  PENDENTE: {
    label: "Pendente",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
    borderClass: "border-amber-200",
    icon: <Clock size={12} />,
  },
  COLETA_EM_ANDAMENTO: {
    label: "Coleta em andamento",
    bgClass: "bg-blue-50",
    textClass: "text-blue-700",
    borderClass: "border-blue-200",
    icon: <PlayCircle size={12} />,
  },
  COLETA_ENCERRADA: {
    label: "Coleta encerrada",
    bgClass: "bg-indigo-50",
    textClass: "text-indigo-700",
    borderClass: "border-indigo-200",
    icon: <StopCircle size={12} />,
  },
  AVALIACAO_EM_PROCESSAMENTO: {
    label: "Avaliação em processamento",
    bgClass: "bg-purple-50",
    textClass: "text-purple-700",
    borderClass: "border-purple-200",
    icon: <Cog size={12} />,
  },
  AVALIACAO_CONCLUIDA: {
    label: "Avaliação concluída",
    bgClass: "bg-emerald-50",
    textClass: "text-emerald-700",
    borderClass: "border-emerald-200",
    icon: <CheckCircle2 size={12} />,
  },
  AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES: {
    label: "Bloqueada (dados insuficientes)",
    bgClass: "bg-red-50",
    textClass: "text-red-700",
    borderClass: "border-red-200",
    icon: <Ban size={12} />,
  },
  AVALIACAO_BLOQUEADA_SEM_RESPOSTAS: {
    label: "Bloqueada (sem respostas)",
    bgClass: "bg-orange-50",
    textClass: "text-orange-700",
    borderClass: "border-orange-200",
    icon: <XCircle size={12} />,
  },
};

/**
 * Componente de badge de status da avaliação.
 * Renderiza exatamente o mesmo visual em Avaliações e Documentos.
 */
export function EvaluationStatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-500 border border-gray-200 rounded-full text-xs font-semibold whitespace-nowrap">
        <HelpCircle size={12} />
        Sem avaliação
      </span>
    );
  }

  const config = EVALUATION_STATUS_CONFIG[status];
  if (!config) {
    return (
      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
        {status}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 ${config.bgClass} ${config.textClass} border ${config.borderClass} rounded-full text-xs font-semibold whitespace-nowrap`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

/** Enum values válidos para o filtro de status */
export type EvaluationStatus =
  | "PENDENTE"
  | "COLETA_EM_ANDAMENTO"
  | "COLETA_ENCERRADA"
  | "AVALIACAO_EM_PROCESSAMENTO"
  | "AVALIACAO_CONCLUIDA"
  | "AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES"
  | "AVALIACAO_BLOQUEADA_SEM_RESPOSTAS";

export const EVALUATION_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Todos os status" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "COLETA_EM_ANDAMENTO", label: "Coleta em andamento" },
  { value: "COLETA_ENCERRADA", label: "Coleta encerrada" },
  { value: "AVALIACAO_EM_PROCESSAMENTO", label: "Avaliação em processamento" },
  { value: "AVALIACAO_CONCLUIDA", label: "Avaliação concluída" },
  { value: "AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES", label: "Bloqueada (dados insuficientes)" },
  { value: "AVALIACAO_BLOQUEADA_SEM_RESPOSTAS", label: "Bloqueada (sem respostas)" },
];
